import {
  composeClassNames,
  ifDefined,
  withDefault,
} from "@faedev/editor-utils";
import { useRouter } from "next/router";
import { useContext, useEffect, useState } from "react";
import constants from "src/constants";
import defaultThumbnail from "src/functions/defaultThumbnail";
import getOg from "src/functions/getOg";
import translate from "src/functions/translate";
import AttachedDocuments from "src/components/AttachedDocuments";
import ClassIntroduction from "src/components/ClassIntroduction";
import ContentSection from "src/components/ContentSection";
import ContentProperties from "src/components/ContentProperties";
import Footer from "src/components/Footer";
import Gallery from "src/components/Gallery";
import Header from "src/components/Header";
import InstructorHighlight from "src/components/InstructorHighlight";
import Page from "src/components/Page";
import PayuCheck from "src/components/PayuCheck";
import Preloader from "src/components/Preloader";
import ValidateContent from "src/components/ValidateContent";
import Footers from "src/components/Footers";
import Headers from "src/components/Headers";
import { sPageClass } from "src/styles/sPageClass.module.scss";
import { UserContext } from "src/contexts/UserContext";
import getProductUserData from "src/functions/getProductUserData";

export default function Class({ dClass }) {
  var { locale: lang } = useRouter();
  const [payuCheckReady, setPayuCheckReady] = useState(false);
  const { loggedUser, refetch, loading } = useContext(UserContext);

  const { isPaid, inCart, activeTrialSubscription } = getProductUserData(
    loggedUser,
    dClass.id,
    dClass.subscription_allowed,
    "lecture"
  );

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payuCheckReady]);

  var classTitle = dClass
    ? dClass.title
    : translate(constants.Marketing.ogTitle, lang);
  var classLabel = dClass
    ? dClass.label
    : translate(constants.Marketing.ogDescription, lang);

  const ogData = getOg(
    lang,
    "lecture",
    classTitle,
    classLabel,
    withDefault(dClass ? dClass.thumbnail : ``, defaultThumbnail()).url
  );

  return (
    <>
      <Headers data={ogData} />
      <Page className={composeClassNames([sPageClass, "page-class"])}>
        <Header />
        <PayuCheck setPayuCheckReady={setPayuCheckReady} />
        {dClass ? (
          <ValidateContent
            element={`Class`}
            blocked={withDefault(dClass.blocked, true)}
            verified={ifDefined(
              dClass.verified,
              (value) => value.verified,
              () => false
            )}
          >
            <ContentSection className={`in-detail`} fluid={`xl`}>
              <ClassIntroduction
                data={dClass}
                inCart={inCart}
                isPaid={isPaid}
                className={`border-bottom`}
                activeTrialSubscription={activeTrialSubscription}
                loggedUserClientId={String(loggedUser?.id)}
                loading={loading}
              />
              <ContentProperties
                className={`border-top`}
                duration={dClass.duration}
                benefits={dClass.benefits}
                contraindications={dClass.contraindications}
                difficulty={dClass.difficulty}
                practice_styles={dClass.practice_styles}
                practice_skills={dClass.practice_skills}
                practice_body_parts={dClass.practice_body_parts}
                practice_equipments={dClass.practice_equipments}
                practice_focuses={dClass.practice_focuses}
                verified={dClass.verified}
              />
              <AttachedDocuments
                data={dClass.document}
                parentPaid={dClass.paid}
                isPaid={isPaid}
              />
            </ContentSection>
            <InstructorHighlight data={dClass.profiles[0]} />
            <Gallery
              data={dClass.gallery}
              title={translate(`Class gallery`, lang)}
              className={`border-top`}
            />
          </ValidateContent>
        ) : (
          <Preloader />
        )}
        <Footer />
      </Page>
      <Footers />
    </>
  );
}

export const getServerSideProps = async (context) => {
  const slug = context.params.slug;
  const dClass = await fetch(`${constants.Paths.apiUrl}/lectures/${slug}`).then(
    (res) => {
      if (res.ok) {
        return res.json();
      } else {
        throw new Error("API Error");
      }
    }
  );
  return {
    props: { dClass },
  };
};
